import type { ControlCenterAsset, ControlCenterJob, ControlCenterMunicipality, ControlCenterPerson } from '../../../types/controlCenter'
import type { OmniCategory } from './controlCenterMapTypes'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faBuilding, faLandmark, faTruck, faUser } from '@fortawesome/free-solid-svg-icons'
import { licenseTypeLabel } from '../../../utils/controlCenterMunicipalityLicenses'
import { assetKindLabel, personKindLabel, titleCase } from './mapEntityPresentation'

export const OMNI_RESULT_CAP_PER_GROUP = 10
export const OMNI_CATEGORY_ORDER: OmniCategory[] = ['job', 'person', 'asset', 'municipality']
export const OMNI_GROUP_LABEL: Record<OmniCategory, string> = {
  job: 'Jobs',
  person: 'People',
  asset: 'Assets',
  municipality: 'Municipalities',
}

export const OMNI_CATEGORY_ICON: Record<OmniCategory, IconDefinition> = {
  job: faBuilding,
  person: faUser,
  asset: faTruck,
  municipality: faLandmark,
}

function omniNormText(...parts: Array<string | number | null | undefined>): string {
  return parts
    .map((part) => (part == null ? '' : String(part)))
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** All query tokens must appear somewhere in the haystack (order-independent). */
export function omniQueryMatches(query: string, haystack: string): boolean {
  const tokens = omniNormText(query).split(' ').filter(Boolean)
  if (tokens.length === 0) return false
  return tokens.every((token) => haystack.includes(token))
}

export function omniNamesLooselyMatch(a: string, b: string): boolean {
  const left = omniNormText(a)
  const right = omniNormText(b)
  if (!left || !right) return false
  return left === right || left.includes(right) || right.includes(left)
}

export function jobOmniHaystack(job: ControlCenterJob): string {
  return omniNormText(
    job.name,
    job.number,
    job.status,
    job.projectType,
    job.locationName,
    job.address,
    job.city,
    job.state,
    job.accountName,
    job.active ? 'active open' : 'inactive closed',
    job.scheduleStart,
    job.scheduleEnd,
  )
}

export function personOmniHaystack(person: ControlCenterPerson): string {
  return omniNormText(
    person.name,
    person.email,
    person.phone,
    person.roleName,
    person.portalName,
    person.personKind,
    personKindLabel(person.personKind),
    person.clockedIn ? 'clocked in' : 'off clock',
    person.jobName,
    person.jobNumber,
    person.locationName,
    person.address,
  )
}

export function assetDriverName(asset: ControlCenterAsset): string {
  return (
    asset.driver ||
    (asset.raw.appDriverName != null ? String(asset.raw.appDriverName) : '') ||
    (asset.raw.driverName != null ? String(asset.raw.driverName) : '') ||
    (asset.raw.driver != null ? String(asset.raw.driver) : '')
  ).trim()
}

export function assetOmniHaystack(asset: ControlCenterAsset): string {
  const raw = asset.raw
  return omniNormText(
    asset.name,
    asset.status,
    asset.assetKind,
    assetKindLabel(asset.assetKind),
    assetDriverName(asset),
    asset.fuelLevel,
    asset.battery,
    asset.fleetId,
    raw.vin != null ? String(raw.vin) : '',
    raw.make != null ? String(raw.make) : '',
    raw.model != null ? String(raw.model) : '',
    raw.year != null ? String(raw.year) : '',
    raw.licensePlate != null ? String(raw.licensePlate) : '',
  )
}

export function municipalityOmniHaystack(muni: ControlCenterMunicipality): string {
  return omniNormText(
    muni.name,
    muni.state,
    muni.level,
    titleCase(muni.level),
    muni.licenseType,
    licenseTypeLabel(muni.licenseType),
    muni.licenseNumber,
    muni.qualifier,
    muni.expirationDate,
    muni.obtainedDate,
    muni.isExternal ? 'unlicensed external not in db census' : 'licensed',
  )
}

